import { getAuthorizedFetchOptions } from '../../../helpers/getAuthorizedFetchOptions';
import { ClientInfo, defaultClientInfo } from '../clientInfo';
import { CaseSummary } from '../caseDetails/caseSummary';
import { caseLink } from '../caseDetails/formatCase';
import { ConversationSession } from '../conversation/types';
import { Preset } from '../presets/types';
import { TriageCase, TriageUser } from '../triage/types';
import { LlmCaseContext, LlmPayload, LlmResult, LlmService } from './types';

export const DEFAULT_MAX_TRANSCRIPT = 50;
/** Longer than the backend's own model timeout (30 s), so a slow answer is a 504 from the API, not a client abort. */
export const DEFAULT_TIMEOUT_MS = 45000;
export const MAX_RECENT_CASES = 6;

/** "Jane Doe" -> "J.D." The model only needs initials to tell cases apart. */
export function patientInitials(name?: string): string | undefined {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return undefined;
  return parts.map((p) => `${p[0].toUpperCase()}.`).join('');
}

/** Pure: the slice of a case the model may see. */
export function toLlmCaseContext(c: CaseSummary): LlmCaseContext {
  return {
    id: c.id,
    caseRefNo: c.caseRefNo,
    patientInitials: patientInitials(c.patientName),
    treatments: c.treatments || [],
    statusLabel: c.statusLabel,
    latestProvider: c.latestProvider,
    latestStatusLabel: c.latestStatusLabel,
    lastUpdated: c.lastUpdated,
    returnDate: c.returnDate,
    expectedReturnDate: c.expectedReturnDate,
    expectedReturnSource: c.expectedReturnSource,
    outputType: c.outputType,
    flags: c.flags,
    link: caseLink(c) || undefined,
  };
}

export interface BuildLlmPayloadArgs {
  session: ConversationSession;
  user: TriageUser;
  userMessage: string;
  presets: Pick<Preset, 'label' | 'showInMenu'>[];
  selectedCase?: TriageCase;
  sentiment?: LlmPayload['sentiment'];
  /** Full details of the selected case and of recent cases, when the widget has them. */
  cases?: { selected?: CaseSummary | null; recent?: CaseSummary[] };
  maxTranscript?: number;
  now?: () => number;
  client?: Partial<ClientInfo>;
}

/** Pure: turns the current session into the payload the LLM API receives. */
export function buildLlmPayload({
  session,
  user,
  userMessage,
  presets,
  selectedCase,
  sentiment,
  cases,
  maxTranscript = DEFAULT_MAX_TRANSCRIPT,
  now = Date.now,
  client,
}: BuildLlmPayloadArgs): LlmPayload {
  const recent = session.messages.slice(-maxTranscript);
  const recentCases = (cases?.recent || []).slice(0, MAX_RECENT_CASES).map(toLlmCaseContext);
  const caseContext = cases?.selected || recentCases.length > 0
    ? { ...(cases?.selected ? { selected: toLlmCaseContext(cases.selected) } : {}), ...(recentCases.length > 0 ? { recent: recentCases } : {}) }
    : undefined;
  return {
    schemaVersion: 2,
    sessionId: session.id,
    createdAt: new Date(now()).toISOString(),
    user,
    selectedCase,
    transcript: recent.map((m) => ({
      role: m.role,
      text: m.text,
      createdAt: new Date(m.createdAt).toISOString(),
      intentId: m.intentId,
    })),
    userMessage,
    capabilities: presets.filter((p) => p.showInMenu).map((p) => p.label),
    ...(sentiment ? { sentiment } : {}),
    ...(caseContext ? { caseContext } : {}),
    client: { ...defaultClientInfo(), ...(client || {}) },
  };
}

export interface CreateLlmServiceOptions {
  endpoint?: string;
  fetchImpl?: typeof fetch;
  logger?: Pick<Console, 'info' | 'warn'>;
  timeoutMs?: number;
}

const pickReply = (body: any): string => {
  const candidate = body?.reply ?? body?.answer ?? body?.message ?? body?.text;
  return typeof candidate === 'string' ? candidate.trim() : '';
};

const pickEscalation = (body: any): Pick<LlmResult, 'escalate' | 'summary'> => {
  if (body?.escalate !== true) return {};
  const summary = typeof body?.summary === 'string' ? body.summary.trim() : '';
  return { escalate: true, ...(summary ? { summary } : {}) };
};

/**
 * Sends unmatched messages to `REACT_APP_CHAT_LLM_END_POINT` and returns the model's reply.
 * With no endpoint configured it logs the payload and reports `mode: 'noop'` with no reply, so the
 * engine falls back to the canned answer. Never throws: failures come back as `{ accepted: false }`.
 */
export function createLlmService({
  endpoint = process.env.REACT_APP_CHAT_LLM_END_POINT || '',
  fetchImpl,
  logger = console,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: CreateLlmServiceOptions = {}): LlmService {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);

  return {
    async ask(payload) {
      const target = (endpoint || '').trim();
      if (!target) {
        logger.info('[askAi] llm request (no endpoint configured, not sent)', payload);
        return { accepted: false, mode: 'noop' };
      }
      if (!doFetch) {
        return { accepted: false, mode: 'remote', error: 'fetch unavailable' };
      }

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
      const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
      try {
        const options = { ...getAuthorizedFetchOptions('POST', payload), signal: controller?.signal } as RequestInit;
        const response = await doFetch(target, options);
        if (!response.ok) {
          return { accepted: false, mode: 'remote', error: `HTTP ${response.status}` };
        }
        let body: any = null;
        try {
          body = await response.json();
        } catch {
          return { accepted: false, mode: 'remote', error: 'invalid JSON response' };
        }
        const reply = pickReply(body);
        if (!reply) return { accepted: false, mode: 'remote', error: 'empty reply' };
        return { accepted: true, mode: 'remote', reply, ...pickEscalation(body) };
      } catch (error: any) {
        const aborted = error?.name === 'AbortError';
        logger.warn('[askAi] llm request failed', error);
        return { accepted: false, mode: 'remote', error: aborted ? `timeout after ${timeoutMs}ms` : error?.message || 'request failed' };
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
  };
}

/** Service used when no LLM is wired in: never answers, so the engine keeps its canned fallback. */
export const noopLlmService: LlmService = {
  async ask() {
    return { accepted: false, mode: 'noop' };
  },
};
